import { logError } from '@databases/logs';
import { runDatabaseAiField } from '@databases/rovo-bridge';
import type { AiSummaryFieldConfig } from '@databases/types/database';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

type RunAiSummaryFieldOptions = {
  contentId: string;
  fieldId: string;
  fieldName: string;
  config: AiSummaryFieldConfig;
  locale: string;
};

export function buildAiSummaryPrompt(
  fieldName: string,
  config: AiSummaryFieldConfig
): string {
  return [
    `Edit the database by filling the existing "${fieldName}" AI Summary field for every existing row.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    `If a row has no meaningful information, leave the "${fieldName}" value blank.`,
    `Only edit the "${fieldName}" field. Do not create, remove, rename, or reconfigure fields; add or remove rows; edit views; or change any other field values.`,
    'Return the edits using the traditional AIFC Confluence database update CSV response format. Use edit_entry operations and the existing entry_index values.'
  ].join('\n\n');
}

export async function runAiSummaryField({
  contentId,
  fieldId,
  fieldName,
  config,
  locale
}: RunAiSummaryFieldOptions): Promise<void> {
  const isEnabled = getExperimentValue('databasesFrontendAiFieldsSummary', 'enabled', {
    fireExperimentExposure: false
  });
  if (!isEnabled) {
    return;
  }

  try {
    await runDatabaseAiField({
      contentId,
      locale,
      prompt: buildAiSummaryPrompt(fieldName, config),
      target: { aiFieldType: 'summary', fieldId }
    });
  } catch (error) {
    logError(
      'Failed to generate AI Summary field',
      error instanceof Error ? error : new Error(String(error))
    );
    throw error;
  }
}
